import getRouterBasename from '@/lib/router';
import App from 'App';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useApi,
  useAuth,
  useChatInteract,
  useConfig
} from '@chainlit/react-client';
import { ChainlitContext } from '@chainlit/react-client';
import { Loader } from '@/components/Loader';

export default function AppWrapper() {
  const [translationLoaded, setTranslationLoaded] = useState(false);
  const [isAuthenticatingHeader, setIsAuthenticatingHeader] = useState(false);
  const { data: authConfig, isAuthenticated, isReady, setUserFromAPI } = useAuth();
  const { language: languageInUse } = useConfig();
  const { i18n } = useTranslation();
  const { windowMessage } = useChatInteract();
  const apiClient = useContext(ChainlitContext);

  function handleChangeLanguage(languageBundle: any): void {
    i18n.addResourceBundle(languageInUse, 'translation', languageBundle);
    i18n.changeLanguage(languageInUse);
  }

  const { data: translations } = useApi<any>(
    `/project/translations?language=${languageInUse}`
  );

  useEffect(() => {
    if (!translations) return;
    handleChangeLanguage(translations.translation);
    setTranslationLoaded(true);
  }, [translations]);

  useEffect(() => {
    const handleWindowMessage = (event: MessageEvent) => {
      windowMessage(event.data);
    };
    window.addEventListener('message', handleWindowMessage);
    return () => window.removeEventListener('message', handleWindowMessage);
  }, [windowMessage]);

  if (!translationLoaded) return null;

  const isLoginPage =
    window.location.pathname === getRouterBasename() + '/login' ||
    window.location.pathname === getRouterBasename() + '/login/callback';

  if (isReady && !isAuthenticated && !isLoginPage) {
    if (authConfig?.headerAuth) {
      if (!isAuthenticatingHeader) {
        setIsAuthenticatingHeader(true);
        apiClient
          .headerAuth()
          .then((json) => {
            if (json?.success === true) {
              setUserFromAPI();
            } else {
              throw new Error('Header auth failed');
            }
          })
          .catch((error) => {
            console.error(error);
            if (authConfig?.custom_login_url) {
              window.location.href = authConfig.custom_login_url;
            } else {
              window.location.href = getRouterBasename() + '/login';
            }
          });
          // Note: We deliberately do not set isAuthenticatingHeader to false
          // in a finally block to avoid a flash of redirection logic before
          // setUserFromAPI state update propagates. The user state will change,
          // making isAuthenticated true, which will naturally unmount this branch.
      }

      return (
        <div className="flex h-screen w-screen items-center justify-center">
          <Loader className="!size-6" />
        </div>
      );
    }

    if (authConfig?.custom_login_url) {
      window.location.href = authConfig.custom_login_url;
    } else {
      window.location.href = getRouterBasename() + '/login';
    }
    return null;
  }
  return <App />;
}
